import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../../../firebase/config'; 
import { collection, query, doc, deleteDoc, onSnapshot } from 'firebase/firestore';

// Componentes y utilidades locales
import FormularioAumentosArt from '../../../Formularios/FormularioAumentosArt';
import ModalNotificacion from '../../../ui/ModalNotificacion'; 
import ModalConfirmacion from '../../../ui/ModalConfirmacion'; 
import { getPersonaChipClass } from '../../../../utils/styles';

// Icono decorativo de tendencia
const IconTrendUp = () => (
    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-9 9-4-4-6 6" />
    </svg>
);

/**
 * AumentosTab - Visualización y gestión en tiempo real de los aumentos de alícuotas del contrato de ART.
 * Versión limpia: Consume directamente el contrato y cliente pasados por props de forma nativa.
 */
const AumentosTab = ({ contrato, client }) => {
    const [aumentos, setAumentos] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [editingAumento, setEditingAumento] = useState(null);
    const [expandedAumentoId, setExpandedAumentoId] = useState(null);

    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false, itemToDelete: null });

    const contratoId = contrato?.id;
    const clientId = client?.id;

    // --- LISTENER EN TIEMPO REAL (onSnapshot) ---
    useEffect(() => {
        if (!contratoId || !clientId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const aumentosRef = collection(db, `public/data/clientes/${clientId}/contratos/${contratoId}/aumentos_art`);
        const q = query(aumentosRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Ordenar localmente por fecha de carga de forma descendente (Más reciente primero)
            const sorted = data.sort((a, b) => {
                const dateA = a.fechaCarga || '';
                const dateB = b.fechaCarga || '';
                return dateB.localeCompare(dateA);
            });

            setAumentos(sorted);
            setLoading(false);
        }, (err) => {
            console.error("Error en listener de aumentos ART:", err);
            setNotification({ show: true, message: 'Error al sincronizar la lista de aumentos en tiempo real.', type: 'error' });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [contratoId, clientId]);

    const formatCurrency = (val) => {
        if (val === undefined || val === null || val === '') return '-';
        const num = typeof val === 'string' ? parseFloat(val.replace(/\./g, '').replace(',', '.')) : val;
        if (isNaN(num)) return '-';
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(num);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const parts = dateString.split('-');
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
    };

    const formatPercent = (val) => {
        if (val === undefined || val === null || val === '') return '-';
        const raw = String(val).replace('.', ',');
        return raw.endsWith('%') ? raw : `${raw}%`;
    };

    const formatAumentoImpacto = (val) => {
        const num = parseFloat(val) || 0;
        if (num === 0) return '0%';
        const prefix = num > 0 ? '+' : '';
        return `${prefix}${String(num).replace('.', ',')}%`;
    };

    const confirmDelete = (aumento) => {
        setDeletionConfirm({ show: true, itemToDelete: aumento });
    };

    const handleDelete = async () => {
        const aumentoId = deletionConfirm.itemToDelete.id;
        const endosoNum = deletionConfirm.itemToDelete.numeroEndoso || aumentoId.substring(0, 5);
        setDeletionConfirm({ show: false, itemToDelete: null });

        try {
            const docRef = doc(db, `public/data/clientes/${clientId}/contratos/${contratoId}/aumentos_art`, aumentoId);
            await deleteDoc(docRef);
            setNotification({ show: true, message: `El endoso de aumento N° ${endosoNum} ha sido eliminado correctamente.`, type: 'success' });
        } catch (error) {
            console.error("Error al eliminar aumento ART:", error);
            setNotification({ show: true, message: 'Ocurrió un error al intentar eliminar el registro de aumento.', type: 'error' });
        }
    };

    const handleModalSuccess = (msg) => {
        setEditingAumento(null);
        setShowModal(false);
        setNotification({ show: true, message: msg, type: 'success' });
    };

    const toggleRowExpansion = (id) => {
        setExpandedAumentoId(expandedAumentoId === id ? null : id);
    };

    return (
        <div className="space-y-6 animate-fade-in font-sans text-left pb-10">
            
            {/* Header */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center shadow-sm relative overflow-hidden shrink-0">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-orange-500"></div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase">Historial de Aumentos ART</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Control de variaciones y endosos de aumentos</p>
                </div>
                <button 
                    onClick={() => { setEditingAumento(null); setShowModal(true); }}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                    Cargar Aumento
                </button>
            </div>

            {/* Tabla de Aumentos */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                <table className="w-full text-left border-collapse text-[13px] min-w-[1200px]">
                    <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest">
                        <tr>
                            <th className="p-4 w-12 text-center"></th>
                            <th className="p-4 w-28 whitespace-nowrap">F. Carga</th>
                            <th className="p-4 w-32 text-center">Mes Aumento</th>
                            <th className="p-4 w-32 text-center">Periodo ARCA</th>
                            <th className="p-4 w-32 text-center">N° Endoso</th>
                            <th className="p-4 w-24 text-center">Capitas</th>
                            <th className="p-4 w-40 text-right">Masa Salarial</th>
                            <th className="p-4 w-28 text-center">Alic. Act.</th>
                            <th className="p-4 w-28 text-center">Alic. Aum.</th>
                            <th className="p-4 w-24 text-center">% Aum.</th>
                            <th className="p-4 w-32 text-center">Estado</th>
                            <th className="p-4 w-40 text-center">Responsable</th>
                            <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-5px_0_10px_rgba(0,0,0,0.3)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="13" className="p-16 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Sincronizando aumentos de ART...</td></tr>
                        ) : aumentos.length === 0 ? (
                            <tr><td colSpan="13" className="p-16 text-center text-slate-400 italic font-medium">No hay registros de aumentos vinculados.</td></tr>
                        ) : (
                            aumentos.map(item => {
                                const isPositive = parseFloat(item.porcentajeAumento) > 0;
                                return (
                                    <React.Fragment key={item.id}>
                                        <tr 
                                            onClick={() => toggleRowExpansion(item.id)}
                                            className={`hover:bg-slate-50 transition-colors cursor-pointer group ${expandedAumentoId === item.id ? 'bg-orange-50/20' : ''}`}
                                        >
                                            <td className="p-4 text-center">
                                                <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${expandedAumentoId === item.id ? 'rotate-90 text-orange-600' : 'text-slate-300 group-hover:text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path></svg>
                                            </td>
                                            <td className="p-4 font-bold text-slate-500 tabular-nums whitespace-nowrap">
                                                {formatDate(item.fechaCarga)}
                                            </td>
                                            <td className="p-4 text-center font-bold text-slate-700 font-mono">
                                                {item.mesAumento || '-'}
                                            </td>
                                            <td className="p-4 text-center font-bold text-slate-700 font-mono">
                                                {item.periodoArca || '-'}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-orange-700 uppercase tracking-tighter">
                                                {item.numeroEndoso || '-'}
                                            </td>
                                            <td className="p-4 text-center font-bold text-slate-800 tabular-nums">
                                                {item.capitas || '0'}
                                            </td>
                                            <td className="p-4 text-right font-mono font-bold text-slate-800">
                                                {formatCurrency(item.masaSalarial)}
                                            </td>
                                            <td className="p-4 text-center font-mono font-semibold text-slate-700">
                                                {formatPercent(item.alicuotaActual)}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-orange-800">
                                                {formatPercent(item.alicuotaAumentada)}
                                            </td>
                                            <td className="p-4 text-center whitespace-nowrap">
                                                <span className={`px-2 py-0.5 rounded text-[11px] font-black font-mono ${isPositive ? 'bg-orange-50 text-orange-700 border border-orange-100' : 'bg-slate-50 text-slate-500'}`}>
                                                    {formatAumentoImpacto(item.porcentajeAumento)}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center whitespace-nowrap">
                                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${
                                                    item.estado === 'Aprobado' || item.estado === 'APROBADO' ? 'bg-green-50 text-green-700 border-green-200' :
                                                    item.estado === 'Rechazado' || item.estado === 'RECHAZADO' ? 'bg-red-50 text-red-700 border-red-200' :
                                                    'bg-amber-50 text-amber-700 border-amber-200'
                                                }`}>
                                                    {item.estado || 'Pendiente'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center whitespace-nowrap">
                                                {item.usuarioNombre ? (
                                                    <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(item.usuarioNombre)}`}>
                                                        {item.usuarioNombre}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className={`p-4 text-right pr-6 sticky right-0 z-10 transition-colors shadow-[-5px_0_10px_rgba(0,0,0,0.02)] ${expandedAumentoId === item.id ? 'bg-[#fffbf8]' : 'bg-white group-hover:bg-slate-50'}`}>
                                                <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={(e) => { e.stopPropagation(); setEditingAumento(item); setShowModal(true); }} className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100" title="Editar aumento"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                    <button onClick={(e) => { e.stopPropagation(); confirmDelete(item); }} className="p-1.5 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100" title="Eliminar aumento"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                                </div>
                                            </td>
                                        </tr>
                                        
                                        {/* Fila Oculta: Observaciones */}
                                        {expandedAumentoId === item.id && (
                                            <tr className="bg-slate-50/50 border-b border-slate-100 animate-slide-down">
                                                <td colSpan="13" className="px-12 py-6 text-left">
                                                    <div className="flex flex-col gap-2 max-w-4xl">
                                                        <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-widest flex items-center gap-2 mb-1">
                                                            <IconTrendUp /> Notas y comentarios de la solicitud
                                                        </h4>
                                                        <div className="pl-4 border-l-4 border-orange-200">
                                                            <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                                                                "{item.observaciones || 'No se registraron observaciones adicionales para este aumento.'}"
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                .animate-slide-down { animation: slideDown 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {/* Modal de Carga / Edición integrado renderizado con PORTAL al body */}
            {showModal && createPortal(
                <div className="fixed inset-0 z-[9999] overflow-y-auto bg-slate-950/60 backdrop-blur-sm flex justify-center items-center p-4 md:p-6 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar animate-slide-down relative flex flex-col my-auto">
                        <button 
                            onClick={() => setShowModal(false)}
                            className="absolute right-6 top-6 text-slate-400 hover:text-slate-800 font-bold text-xl p-1 transition-colors z-[210]"
                        >
                            ✕
                        </button>
                        <div className="p-1">
                            <FormularioAumentosArt 
                                entityData={client}
                                contractData={contrato}
                                initialData={editingAumento}
                                onSuccess={handleModalSuccess}
                                onCancel={() => setShowModal(false)}
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {notification.show && createPortal(
                <ModalNotificacion
                    show={notification.show}
                    onClose={() => setNotification({ show: false, message: '', type: 'success' })}
                    message={notification.message}
                    type={notification.type}
                />,
                document.body
            )}
            
            {deletionConfirm.show && createPortal(
                <ModalConfirmacion
                    show={deletionConfirm.show}
                    onClose={() => setDeletionConfirm({ show: false, itemToDelete: null })}
                    onConfirm={handleDelete}
                    title="Eliminar Solicitud de Aumento"
                    message={`¿Estás seguro de que deseas eliminar la solicitud de aumento de alícuota ART N° ${deletionConfirm.itemToDelete?.numeroEndoso || deletionConfirm.itemToDelete?.id?.substring(0, 5) || 'seleccionada'}? Esta acción es irreversible.`}
                />,
                document.body
            )}
        </div>
    );
};

export default AumentosTab;